/**
 * SEC-4: sessão expirada / 401 no painel → limpar storage e ir ao login **uma vez**.
 * Evita loop: ignora /login e rotas públicas de auth; flag em memória + sessionStorage.
 */
import { isDelideskEmbed } from "./delideskEmbed";

const REDIRECT_FLAG = "delivai_session_expired_redirect";

// Dev usa /api/n8n/auth/... (proxy); prod usa .../webhook/auth/...
const AUTH_PATH_SKIP =
  /\/(?:webhook\/)?auth\/(verify-password|me|logout|refresh|check-cnpj|gerate-code|verification-code|create-password)/i;

function pathIsLogin(): boolean {
  if (typeof window === "undefined") return true;
  const p = window.location.pathname || "";
  return (
    p === "/login" ||
    p.startsWith("/login/") ||
    p === "/setup" ||
    p.startsWith("/setup") ||
    p.startsWith("/delidesk-sso") ||
    p.startsWith("/autorizar") ||
    p.startsWith("/agendamento") ||
    p.startsWith("/catalog") ||
    p.startsWith("/pedido") ||
    p.startsWith("/cardapio") ||
    p.startsWith("/presente") ||
    p.startsWith("/schedule")
  );
}

function shouldSkipUrl(url: string): boolean {
  if (AUTH_PATH_SKIP.test(url)) return true;
  if (/\/colaboradores\/(auth-password|validate-invite|resolve-login)/i.test(url)) {
    return true;
  }
  // Token Google expirado / ausente ≠ sessão do painel. Agenda do colaborador
  // chama /api/google no mount; 401 daqui não pode devolver ao /login.
  if (/\/api\/google(\/|$)/i.test(url)) {
    return true;
  }
  return false;
}

function clearPanelLocalSession(): void {
  try {
    localStorage.removeItem("delivai_user");
    localStorage.removeItem("delivai_collaborator_cpf");
    localStorage.removeItem("license_modules");
    localStorage.removeItem("evolution_token");
    localStorage.removeItem("evolution_user");
  } catch {
    /* ignore */
  }
}

/** Chamar em 401 de rotas do painel (apiClient / panelFetch). */
export function handlePanelUnauthorized(requestUrl?: string): void {
  if (typeof window === "undefined") return;
  if (requestUrl && shouldSkipUrl(requestUrl)) return;
  if (pathIsLogin()) return;
  // BrowserView do DeliDesk: 401 cedo (cookie ainda gravando) não pode
  // limpar localStorage nem ir a /login — isso desconectava o app inteiro.
  if (isDelideskEmbed()) return;
  // Evita bounce login→dashboard→login quando o cookie ainda não gravou / 401 cedo demais.
  try {
    const until = Number(sessionStorage.getItem("delivai_auth_grace_until") || "0");
    if (until && Date.now() < until) return;
  } catch {
    /* ignore */
  }
  try {
    if (sessionStorage.getItem(REDIRECT_FLAG) === "1") return;
    sessionStorage.setItem(REDIRECT_FLAG, "1");
  } catch {
    /* ignore */
  }
  clearPanelLocalSession();
  const next = `${window.location.pathname}${window.location.search || ""}`;
  const q =
    next && next !== "/login"
      ? `?redirect=${encodeURIComponent(next)}`
      : "";
  window.location.replace(`/login${q}`);
}

/** Após login OK: não tratar 401 como “sessão expirada” por alguns segundos. */
export function armAuthGracePeriod(ms = 8_000): void {
  try {
    sessionStorage.setItem("delivai_auth_grace_until", String(Date.now() + ms));
    sessionStorage.removeItem(REDIRECT_FLAG);
  } catch {
    /* ignore */
  }
}

/** Chamar no boot da tela de login para permitir novo ciclo após login OK. */
export function clearSessionExpiredRedirectFlag(): void {
  try {
    sessionStorage.removeItem(REDIRECT_FLAG);
    sessionStorage.removeItem("delivai_auth_grace_until");
  } catch {
    /* ignore */
  }
}
